import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Search, FileSignature, Wallet, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Nuova tassonomia Fornitori V1
const CATEGORIES = [
  { value: 'subappaltatore_edile', label: 'Subappaltatore edile' },
  { value: 'elettricista', label: 'Elettricista' },
  { value: 'idraulico', label: 'Idraulico' },
  { value: 'serramentista', label: 'Serramentista' },
  { value: 'fornitore_materiali', label: 'Fornitore materiali' },
  { value: 'fornitore_arredi', label: 'Fornitore arredi' },
  { value: 'studio_tecnico_esterno', label: 'Studio tecnico esterno' },
  { value: 'professionista', label: 'Professionista' },
  { value: 'altro', label: 'Altro' },
];

// Alias legacy (per anagrafiche create prima di Fornitori V1)
const LEGACY_LABELS = {
  edile: 'Subappaltatore edile',
  elettrico: 'Elettricista',
  serramenti: 'Serramentista',
  finiture: 'Altro',
  impianti: 'Altro',
};

const categoryLabel = (value) => {
  if (!value) return '—';
  return (
    CATEGORIES.find((c) => c.value === value)?.label ||
    LEGACY_LABELS[value] ||
    value
  );
};

const formatCurrency = (v) => {
  const n0 = Number(v || 0);
  const n = Math.round(n0 * 100) === 0 ? 0 : n0;
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
};

const YEAR_ALL = 'all';
const DEFAULT_FALLBACK_YEARS = (() => {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2, now - 3];
})();

const SuppliersPage = () => {
  const [aggregate, setAggregate] = useState({ items: [], summary: {}, year: null, is_all_years: true, available_years: DEFAULT_FALLBACK_YEARS });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Default: "Tutte le annualità" (aggregato totale)
  const [year, setYear] = useState(YEAR_ALL);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (categoryFilter && categoryFilter !== 'all') params.set('category', categoryFilter);
        params.set('year', String(year)); // 'all' oppure numero
        const r = await axios.get(`${API}/api/suppliers/aggregate?${params}`);
        setAggregate(r.data || { items: [], summary: {}, year: null, is_all_years: true, available_years: DEFAULT_FALLBACK_YEARS });
      } catch (e) {
        toast.error('Errore caricamento anagrafica Fornitori');
        setAggregate({ items: [], summary: {}, year: null, is_all_years: true, available_years: DEFAULT_FALLBACK_YEARS });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [query, categoryFilter, year, refreshKey]);

  const items = aggregate.items || [];
  const summary = aggregate.summary || {};
  const isAllYears = aggregate.is_all_years === true || year === YEAR_ALL;
  const yearsList = (aggregate.available_years && aggregate.available_years.length ? aggregate.available_years : DEFAULT_FALLBACK_YEARS);

  // Label helpers dinamici
  const periodLabel = isAllYears ? 'totali' : String(aggregate.year || year);
  const periodShortLabel = isAllYears ? '' : String(aggregate.year || year);
  const cardAffidamentiLabel = isAllYears ? 'Affidamenti totali' : `Affidamenti ${periodShortLabel}`;
  const cardTotaleAffidatoLabel = isAllYears ? 'Totale affidato' : `Totale affidato ${periodShortLabel}`;
  const cardUltimoCantiereLabel = isAllYears ? 'Ultimo cantiere' : `Ultimo cantiere ${periodShortLabel}`;
  const tableAffidatoLabel = isAllYears ? 'Affidato totale' : `Affidato ${periodShortLabel}`;
  const tableContrattiLabel = isAllYears ? 'N. contratti' : `N. contratti ${periodShortLabel}`;

  const handleDelete = async (s) => {
    if (!window.confirm(`Eliminare ${s.ragione_sociale}?`)) return;
    try {
      await axios.delete(`${API}/api/suppliers/${s.id}`);
      toast.success('Fornitore eliminato');
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const noData = !loading && items.length === 0;

  return (
    <div className="p-6 space-y-4" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#3C3C3C]">Fornitori</h1>
          <p className="text-sm text-[#737373]">
            Anagrafica globale · subappaltatori, impiantisti, fornitori, professionisti · {items.length} attivi
          </p>
        </div>
        <Button
          onClick={() => { setEditingSupplier(null); setDialogOpen(true); }}
          className="bg-[#FF7819] hover:bg-[#e56811] text-white"
          data-testid="new-supplier-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Fornitore
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="suppliers-summary-cards">
        <SummaryCard
          testId="card-affidamenti"
          icon={<FileSignature className="w-5 h-5 text-[#FF7819]" />}
          label={cardAffidamentiLabel}
          value={summary.total_contracts_year ?? 0}
          hint={
            (summary.total_contracts_year ?? 0) === 0
              ? 'Disponibile dopo firma subappalti'
              : (isAllYears ? 'Contratti firmati (tutte le annualità)' : 'Contratti firmati nell\u2019anno')
          }
        />
        <SummaryCard
          testId="card-totale-affidato"
          icon={<Wallet className="w-5 h-5 text-[#FF7819]" />}
          label={cardTotaleAffidatoLabel}
          value={formatCurrency(summary.total_assigned_year || 0)}
          hint={
            (summary.total_assigned_year || 0) === 0
              ? 'Disponibile dopo firma subappalti'
              : (isAllYears ? 'Somma total_amount_net contratti firmati' : 'Somma total_amount_net contratti firmati')
          }
        />
        <SummaryCard
          testId="card-ultimo-cantiere"
          icon={<Building2 className="w-5 h-5 text-[#FF7819]" />}
          label={cardUltimoCantiereLabel}
          value={
            summary.last_project_number && summary.last_project_id ? (
              <Link
                to={`/projects/${summary.last_project_id}`}
                className="text-[#FF7819] hover:underline"
                data-testid="card-ultimo-cantiere-link"
              >
                {summary.last_project_number}
              </Link>
            ) : (summary.last_project_number || '—')
          }
          hint={
            summary.last_project_number
              ? 'Cantiere piu\u2019 recente con contratto firmato'
              : 'Disponibile dopo firma subappalti'
          }
        />
      </div>

      {/* Filtri */}
      <div className="flex gap-2 flex-wrap" data-testid="suppliers-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
          <Input
            placeholder="Cerca per nome, P.IVA, C.F..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
            data-testid="supplier-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-56" data-testid="category-filter">
            <SelectValue placeholder="Tipologia" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le tipologie</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={setYear}>
          <SelectTrigger className="w-44" data-testid="year-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={YEAR_ALL} data-testid="year-option-all">Tutte le annualità</SelectItem>
            {yearsList.map((y) => (
              <SelectItem key={y} value={String(y)} data-testid={`year-option-${y}`}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabella */}
      <Card className="card-abitalo">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#737373]">Caricamento...</div>
          ) : noData ? (
            <div className="p-8 text-center text-sm text-[#737373]">
              Nessun fornitore. Clicca &quot;Nuovo Fornitore&quot; per iniziare.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="suppliers-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[#525252] text-xs uppercase">
                    <th className="px-3 py-2 font-semibold">Ragione sociale</th>
                    <th className="px-3 py-2 font-semibold">Tipologia</th>
                    <th className="px-3 py-2 font-semibold">P.IVA</th>
                    <th className="px-3 py-2 font-semibold">Telefono / email</th>
                    <th className="px-3 py-2 font-semibold text-right">{tableAffidatoLabel}</th>
                    <th className="px-3 py-2 font-semibold text-right">{tableContrattiLabel}</th>
                    <th className="px-3 py-2 font-semibold">Ultimo cantiere</th>
                    <th className="px-3 py-2 font-semibold text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const s = row.supplier;
                    return (
                      <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`supplier-row-${s.id}`}>
                        <td className="px-3 py-2 font-medium">{s.ragione_sociale}</td>
                        <td className="px-3 py-2">
                          <Badge variant="outline">{categoryLabel(s.category)}</Badge>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{s.partita_iva}</td>
                        <td className="px-3 py-2 text-xs text-[#525252]">
                          <div>{s.telefono || '—'}</div>
                          <div className="text-[#737373]">{s.email || s.pec || '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-medium" data-testid={`assigned-year-${s.id}`}>
                          {row.contracts_count_year > 0
                            ? formatCurrency(row.assigned_amount_year)
                            : <span className="text-[#a3a3a3]">0,00 €</span>}
                        </td>
                        <td className="px-3 py-2 text-right" data-testid={`contracts-count-${s.id}`}>
                          {row.contracts_count_year || 0}
                        </td>
                        <td className="px-3 py-2 text-xs text-[#525252]">
                          {row.last_project_number && row.last_project_id ? (
                            <Link
                              to={`/projects/${row.last_project_id}`}
                              className="text-[#FF7819] hover:underline font-medium"
                              data-testid={`last-project-link-${s.id}`}
                              title="Apri dettaglio progetto"
                            >
                              {row.last_project_number}
                            </Link>
                          ) : row.last_project_number ? (
                            <span>{row.last_project_number}</span>
                          ) : (
                            <span className="text-[#a3a3a3]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingSupplier(s); setDialogOpen(true); }} data-testid={`edit-${s.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} data-testid={`delete-${s.id}`}>
                            <Trash2 className="w-4 h-4 text-rose-600" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {dialogOpen && (
        <SupplierDialog
          supplier={editingSupplier}
          onClose={() => setDialogOpen(false)}
          onSaved={() => { setDialogOpen(false); setRefreshKey((k) => k + 1); }}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ testId, icon, label, value, hint }) => (
  <Card className="card-abitalo" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#FFF0E5] flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wide text-[#737373] font-semibold">{label}</div>
          <div className="text-2xl font-bold text-[#3C3C3C] mt-0.5 truncate">{value}</div>
          {hint && <div className="text-[11px] text-[#a3a3a3] mt-1">{hint}</div>}
        </div>
      </div>
    </CardContent>
  </Card>
);

const VAT_REGIME_OPTIONS = [
  { value: 'reverse_charge', label: 'Reverse charge' },
  { value: 'vat_4', label: 'IVA 4%' },
  { value: 'vat_10', label: 'IVA 10%' },
  { value: 'vat_22', label: 'IVA 22%' },
  { value: 'exempt', label: 'Esente IVA' },
  { value: 'out_of_scope', label: 'Fuori campo IVA' },
  { value: 'not_subject', label: 'Non soggetto IVA' },
  { value: 'to_verify', label: 'Da verificare' },
];

const DEFAULT_VAT_BY_CATEGORY = {
  subappaltatore_edile: 'reverse_charge',
  elettricista: 'reverse_charge',
  idraulico: 'reverse_charge',
  serramentista: 'vat_22',
  fornitore_materiali: 'vat_22',
  fornitore_arredi: 'vat_22',
  studio_tecnico_esterno: 'vat_22',
  professionista: 'vat_22',
  altro: 'to_verify',
  // legacy
  edile: 'reverse_charge',
  elettrico: 'reverse_charge',
};

// Normalizza valore legacy → nuovo enum (per record esistenti)
const LEGACY_VAT_ALIAS = { iva_10: 'vat_10', iva_22: 'vat_22', fuori_campo: 'out_of_scope', da_verificare: 'to_verify' };
const normalizeVatRegime = (v) => LEGACY_VAT_ALIAS[v] || v || 'to_verify';

const SupplierDialog = ({ supplier, onClose, onSaved }) => {
  // Se il supplier ha una categoria legacy (edile/elettrico/...), la normalizziamo
  // alla nuova taxonomy in fase di editing.
  const legacyMap = { edile: 'subappaltatore_edile', elettrico: 'elettricista', serramenti: 'serramentista', finiture: 'altro', impianti: 'altro' };
  const initialCategory = supplier?.category
    ? (CATEGORIES.find((c) => c.value === supplier.category) ? supplier.category : (legacyMap[supplier.category] || 'altro'))
    : 'altro';

  const [form, setForm] = useState({
    ragione_sociale: '',
    forma_giuridica: '',
    sede_legale: '',
    sede_legale_city: '',
    sede_legale_cap: '',
    sede_legale_province: '',
    codice_fiscale: '',
    partita_iva: '',
    pec: '',
    email: '',
    telefono: '',
    legale_rappresentante: '',
    ccnl: '',
    cassa_edile: '',
    iban: '',
    notes: '',
    ...(supplier || {}),
    category: initialCategory,
    vat_regime: normalizeVatRegime(supplier?.vat_regime),
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Cambio categoria: se vat_regime NON e' stato scelto manualmente (o e' 'to_verify'),
  // applica il default. Altrimenti chiedi conferma prima di sovrascrivere.
  const setCategoryWithVatDefault = (newCat) => {
    const suggested = DEFAULT_VAT_BY_CATEGORY[newCat] || 'to_verify';
    const currentVat = form.vat_regime || 'to_verify';
    const isDefault = currentVat === 'to_verify' || currentVat === DEFAULT_VAT_BY_CATEGORY[form.category];
    if (isDefault || currentVat === suggested) {
      setForm((f) => ({ ...f, category: newCat, vat_regime: suggested }));
    } else {
      const ok = window.confirm(
        `Nuova categoria: default regime fiscale suggerito "${VAT_REGIME_OPTIONS.find(o => o.value === suggested)?.label}".\n\n` +
        `Il fornitore ha attualmente "${VAT_REGIME_OPTIONS.find(o => o.value === currentVat)?.label}" impostato manualmente.\n\n` +
        `Vuoi aggiornare il regime al nuovo default suggerito?`
      );
      if (ok) {
        setForm((f) => ({ ...f, category: newCat, vat_regime: suggested }));
      } else {
        setForm((f) => ({ ...f, category: newCat }));
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.ragione_sociale || !form.partita_iva || !form.codice_fiscale) {
      toast.error('Ragione sociale, P.IVA e C.F. sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      if (supplier?.id) {
        await axios.put(`${API}/api/suppliers/${supplier.id}`, form);
        toast.success('Fornitore aggiornato');
      } else {
        await axios.post(`${API}/api/suppliers`, form);
        toast.success('Fornitore creato');
      }
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="supplier-dialog">
        <DialogHeader>
          <DialogTitle>{supplier ? 'Modifica' : 'Nuovo'} fornitore</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs uppercase text-[#525252] font-semibold">Ragione sociale *</label>
            <Input value={form.ragione_sociale} onChange={(e) => set('ragione_sociale', e.target.value)} data-testid="input-ragione-sociale" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Forma giuridica</label>
            <Input placeholder="es. SRL, SNC, DITTA INDIVIDUALE" value={form.forma_giuridica || ''} onChange={(e) => set('forma_giuridica', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Tipologia</label>
            <Select value={form.category} onValueChange={setCategoryWithVatDefault}>
              <SelectTrigger data-testid="input-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Regime fiscale / IVA</label>
            <Select value={form.vat_regime || 'to_verify'} onValueChange={(v) => set('vat_regime', v)}>
              <SelectTrigger data-testid="input-vat-regime"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VAT_REGIME_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-[#a3a3a3] mt-0.5">
              Default per categoria: {VAT_REGIME_OPTIONS.find(o => o.value === (DEFAULT_VAT_BY_CATEGORY[form.category] || 'to_verify'))?.label}
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">P.IVA *</label>
            <Input value={form.partita_iva} onChange={(e) => set('partita_iva', e.target.value.toUpperCase())} data-testid="input-piva" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Codice Fiscale *</label>
            <Input value={form.codice_fiscale} onChange={(e) => set('codice_fiscale', e.target.value.toUpperCase())} data-testid="input-cf" />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase text-[#525252] font-semibold">Sede legale (via, città, CAP, provincia)</label>
            <Input value={form.sede_legale || ''} onChange={(e) => set('sede_legale', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Legale rappresentante</label>
            <Input value={form.legale_rappresentante || ''} onChange={(e) => set('legale_rappresentante', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">PEC</label>
            <Input value={form.pec || ''} onChange={(e) => set('pec', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Email</label>
            <Input value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">Telefono</label>
            <Input value={form.telefono || ''} onChange={(e) => set('telefono', e.target.value)} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">IBAN</label>
            <Input value={form.iban || ''} onChange={(e) => set('iban', e.target.value.toUpperCase())} />
          </div>
          <div>
            <label className="text-xs uppercase text-[#525252] font-semibold">CCNL</label>
            <Input value={form.ccnl || ''} onChange={(e) => set('ccnl', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="text-xs uppercase text-[#525252] font-semibold">Note</label>
            <Input value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#FF7819] hover:bg-[#e56811] text-white" data-testid="save-supplier-btn">
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SuppliersPage;
